/* eslint-disable max-len */
import { useCallback, useEffect, useRef, useState } from "react";

import { MIXPANEL_EVENTS } from "../../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../../navigation/params";
import { ScratchPayStatus } from "../../../../services/scratchpay/scratchpay.interface";
import { toastMessageActions } from "../../../../state/request";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useScratchPayPurchaseStatus } from "../../../../services/scratchpay/scratchpay.service";
import { userActivityLogger } from "../../../../utils/mixpanel";

const POLLING_TIMEOUT = 20 * 60 * 1000; // 20 minutes

export interface IPollingContext {
  petId: string;
  petName: string;
  petImage?: string;
  planName: string;
}

interface IUseScratchPayPollingParams {
  onPollingEnd?: () => void;
  onDenied?: () => void;
}

interface IUseScratchPayPollingReturn {
  isPolling: boolean;
  startPolling: (transactionId: string, context: IPollingContext) => void;
  cancelPolling: () => void;
}

export const useScratchPayPolling = ({
  onPollingEnd,
  onDenied,
}: IUseScratchPayPollingParams = {}): IUseScratchPayPollingReturn => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { checkStatus, stopPolling, error: pollingError } = useScratchPayPurchaseStatus();

  const [isPolling, setIsPolling] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const timeoutCheckRef = useRef<NodeJS.Timeout | null>(null);

  const cancelPolling = useCallback(() => {
    if (stopPolling) {
      stopPolling();
    }
    if (timeoutCheckRef.current) {
      clearInterval(timeoutCheckRef.current);
      timeoutCheckRef.current = null;
    }
    setIsPolling(false);
    setTransactionId(null);
    pollingStartTimeRef.current = null;
    onPollingEnd?.();
  }, [stopPolling, onPollingEnd]);

  const handleStatusChange = useCallback(
    (status: ScratchPayStatus, context: IPollingContext | null) => {
      const trackingData = {
        pet_id: context?.petId,
        pet_name: context?.petName,
        plan_name: context?.planName,
        unique_transaction_id: transactionId,
        payment_method: "ScratchPay",
      };

      switch (status) {
      case "APPROVED":
        userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_STATUS_APPROVED, trackingData);
        userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_PLAN_CREATED_SUCCESS, trackingData);
        cancelPolling();
        if (context) {
          navigate(`${PAGES_PATH.PET_CARE}?id=${context.petId}&category=TotalCare_Plan`);
        }
        break;

      case "DENIED":
        userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_STATUS_DENIED, trackingData);
        onDenied?.();
        cancelPolling();
        break;

      case "ERROR":
        userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_STATUS_ERROR, trackingData);
        cancelPolling();
        dispatch(
          toastMessageActions.setGlobalAlert({
            severity: "error",
            message: "An error occurred processing your request. Please try again.",
          }),
        );
        break;

      case "EXPIRED":
        userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_STATUS_EXPIRED, trackingData);
        cancelPolling();
        dispatch(
          toastMessageActions.setGlobalAlert({
            severity: "warning",
            message: "Your session has expired. Please start the process again.",
          }),
        );
        break;

      case "CANCELLED":
        userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_STATUS_CANCELLED, trackingData);
        cancelPolling();
        dispatch(
          toastMessageActions.setGlobalAlert({
            severity: "info",
            message: "Payment cancelled. You can choose a different payment option.",
          }),
        );
        break;

      case "IN_PROGRESS":
      default:
        break;
      }
    },
    [cancelPolling, dispatch, navigate, onDenied, transactionId],
  );

  const startPollingFn = useCallback(
    (transactionId: string, context: IPollingContext) => {
      pollingStartTimeRef.current = Date.now();
      setTransactionId(transactionId);
      setIsPolling(true);

      userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_STATUS_POLLING_STARTED, {
        pet_id: context.petId,
        pet_name: context.petName,
        plan_name: context.planName,
        unique_transaction_id: transactionId,
        payment_method: "ScratchPay",
      });

      checkStatus({
        variables: { uniqueTransactionId: transactionId },
        context: { suppressGlobalErrorToast: true },
        onCompleted: (response) => {
          if (response?.scratchpayPurchaseStatus) {
            handleStatusChange(response.scratchpayPurchaseStatus.status, context);
          }
        },
      });

      timeoutCheckRef.current = setInterval(() => {
        if (
          pollingStartTimeRef.current &&
          Date.now() - pollingStartTimeRef.current > POLLING_TIMEOUT
        ) {
          cancelPolling();
          dispatch(
            toastMessageActions.setGlobalAlert({
              severity: "error",
              message: "Request timed out. Please contact support to check your application status.",
            }),
          );
        }
      }, 1000);
    },
    [checkStatus, cancelPolling, dispatch, handleStatusChange],
  );

  useEffect(() => {
    if (pollingError && isPolling) {
      cancelPolling();
      dispatch(
        toastMessageActions.setGlobalAlert({
          severity: "error",
          message: "Unable to check payment status. Please contact support to verify your application.",
        }),
      );
    }
  }, [pollingError, isPolling, cancelPolling, dispatch]);

  useEffect(() => {
    return () => {
      cancelPolling();
    };
  }, [cancelPolling]);

  return {
    isPolling,
    startPolling: startPollingFn,
    cancelPolling,
  };
};

